import { FC, useEffect, useRef, useState } from 'react'
import { KTSVG } from '../../../../../../_metronic/helpers'
import { Formik, Form, FormikValues, FieldArray } from 'formik'
import * as Yup from 'yup'
import { pickBy, filter, sortBy } from 'lodash'
import {
  FormikTextInput,
  FormikTextField,
  FormikDateSelector,
  StatusErrorMessage,
} from '../../../../../components/widgets'


import { getErrorMessage, downloadFile, array_fill_keys } from '../../../../../components/helpers'
import { generateDLMROI, IDLMWebROIModel, DLM_ROI_GENERATE_FIELDS, initDLMWebROIModelValues, SCDFPlanNoSchema } from '../../../../core/doclabel'
import { SubmitButtonRow } from '../../../../../components/layout'


const dlmGenerateROISchema: any = Yup.object({
  roiId: Yup.number().label('ROI ID'),
  roiAttachmentId: Yup.number().label('ROI Attachment ID'),
  inspectionId: Yup.number().label('Inspection ID'),
  siteApplicationId: Yup.number().label('Site Application ID'),
  cocHolderRef: Yup.string().trim().label('CoC Holder Ref'),
  cocHolder: Yup.string().trim().label('CoC Holder').required(),
  cocHolderAddress: Yup.string().trim().label('CoC Holder Address').required(),
  installerCompany: Yup.string().trim().label('Installer Company').required(),
  cocNo: Yup.string().trim().label('cocNo'),
  brand: Yup.string().trim().label('brand'),
  model: Yup.string().trim().label('Model').required(),
  country: Yup.string().trim().label('Country').required(),
  projectName: Yup.string().trim().label('Project Name').required(),
  planNo: SCDFPlanNoSchema,
  series: Yup.string().trim().label('Series'),
  otherRef: Yup.string().trim().label('Other Reference'),
  type: Yup.string().trim().label('Type'),
  no: Yup.number().label('No'),
  quantity: Yup.string().trim().label('Quantity'),
  serial: Yup.string().trim().label('Serial').required(),
  revision: Yup.number().label('Revision'),
  split: Yup.number().label('Split'),
  roiDetails: Yup.array().label('ROI Details'),
  getcbRef: Yup.string().trim().label('CB Reference'),
  hasIssueDate: Yup.boolean(),
  issueDate: Yup.string().trim()
  .when('hasIssueDate', {
    is: true,
    then: (schema) => schema.required('Set Issue Date'),
  }),
})

type Props = {
  baseROI : IDLMWebROIModel
  onSuccess: Function
  doClose: Function
  hasIssueDate?: boolean
}


const GenerateROIModal: FC<Props> = ({baseROI, onSuccess, doClose, hasIssueDate = false}) => {

  const formikRef = useRef<any>(null);
  const [initValues, SetInitValues] = useState<IDLMWebROIModel>({ ...initDLMWebROIModelValues });

  const submitROIModel = async (values: any, actions: FormikValues) => {
    const { setStatus, setSubmitting } = actions;

    
    const payload = pickBy(values, (value, key) => (value && DLM_ROI_GENERATE_FIELDS.indexOf(key) > -1));

    setStatus('');
    setSubmitting(true);

    await generateDLMROI(payload)
    .then( (res: any) => {
      if (res.status !== 200) {
        setStatus('Something happened');
        return;
      }
      const filename = res.headers['content-disposition'].split('filename=')[1].replace(/"/gm,'');
      downloadFile(res.data, filename);

      if (onSuccess) {
        onSuccess(values);
      }

      doClose();
    })
    .catch((err: any) => {
      setStatus(getErrorMessage(err));
    })
    .finally(() => {
      setSubmitting(false);
    })
  }

  useEffect(() => {
    if (! (baseROI)) return;

    const _init = {
      ...initDLMWebROIModelValues,
      ...baseROI,
      roiDetails: sortBy(filter(baseROI.roiDetails, (o : any) => (! o?.deleted)), ['itemNo','id'],['asc','asc']),
      hasIssueDate
    }

    SetInitValues(_init);

    setTimeout(function () {
      if (formikRef.current) formikRef.current.validateForm();
    }, 300)
  }, [baseROI, hasIssueDate]);


  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_modal_generate_roi'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className='modal-dialog modal-dialog-centered mw-750px'>
      <div className='modal-content' style={{backgroundColor: '#f0e0be'}}>
        <div className='modal-header'>
          <div>
            <h2 className='fw-bolder'>
              Generate ROI
            </h2>
            { initValues &&
            <div className="d-flex flex-row flex-wrap align-items-start justify-content-between gap-6 p-2">
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>Series: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.series}</div>
              </div>
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>No: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.no}</div>
              </div>
              {(initValues.split > 0) &&
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>Split: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.split}</div>
              </div>
              }
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>COC No: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.cocNo}</div>
              </div>
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>Brand: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.brand}</div>
              </div>
              <div className="d-flex flex-row flex-center gap-2">
                <label className='fw-bold text-muted fs-7'>Quantity: </label>
                <div className="className='fw-bolder fs-7 text-dark">{initValues.quantity}</div>
              </div>
            </div>
            }
          </div>
          <div
            className='btn btn-icon btn-sm btn-active-icon-danger'
            data-kt-producttypes-modal-action='close'
            style={{cursor: 'pointer'}}
            onClick={() => doClose()}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
        <div className='modal-body scroll-y py-4 min-h-80px'>
          <Formik
              innerRef={formikRef}
              initialValues={initValues}
              validationSchema={dlmGenerateROISchema}
              enableReinitialize={true}
              onSubmit={submitROIModel}
              validateOnMount={true}
              initialTouched={{
                ...array_fill_keys([ 'cocHolder', 'cocHolderAddress', 'installerCompany', 'model', 'country', 'projectName', 'planNo', 'serial', ], true ),
                issueDate: hasIssueDate
              }}
          >
          {({status, values}) => (
            <Form id='kt_doclabel_submitresponse_form' className='form' noValidate>
              <div className='d-flex flex-column' id='kt_doclabel_submitresponse_scroll' >
                <div className="row mb-2 gy-2">
                  <FormikTextInput size="12 col-md-4" label="CoC Holder Ref" name="cocHolderRef" />
                  <FormikTextInput size="12 col-md-4" label="CoC Holder" name="cocHolder" required={true} />
                  {hasIssueDate &&
                    <FormikDateSelector size="12 col-md-4" label="Set Issue Date" name="issueDate" required={true} />
                  }
                </div>
                <div className="row mb-2 gy-2">
                  <FormikTextField size="12 col-md-8" label="CoC Holder Address" name="cocHolderAddress" required={true} rows={1} />
                  <FormikTextInput size="12 col-md-4" label="Country" name="country" required={true} />
                </div>
                <div className="row mb-2 gy-2">
                  <FormikTextField size="12" label="Project Name" name="projectName" required={true} rows={3} />
                </div>
                <div className="row mb-2 gy-2">
                  <FormikTextInput size="12 col-md-4" label="Installer Company" name="installerCompany" required={true} />
                  <FormikTextInput size="12 col-md-4" label="Model" name="model" required={true} />
                  <FormikTextInput size="12 col-md-4" label="SCDF Building Plan No" name="planNo" />
                  <FormikTextInput size="12 col-md-4" label="Serial" name="serial" required={true} />
                  <FormikTextInput size="12 col-md-4" label="Other Reference" name="otherRef" />
                  <FormikTextInput size="12 col-md-4" label="Type" name="type" />
                </div>
                <FieldArray
                  name="roiModels"
                  render={ arrayHelpers => (
                  <>
                  <h4 className="mt-4">Item Details</h4>
                  <div className='separator border-gray-200 mb-2'></div>
                  <div className='table-responsive min-h-80px mh-150px'>
                    <table id='kt_table_genroi_details' className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer' >
                      <thead>
                        <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                          <th className="p-2">No</th>
                          <th className="p-2">Quantity</th>
                          <th className="p-2">Size W x H  (mm) / Model</th>
                          <th className="p-2"></th>
                        </tr>
                        <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                          <th className="p-2">From</th>
                          <th className="p-2">To</th>
                          <th className="p-2">Type</th>
                          <th className="p-2">Remarks</th>
                        </tr>
                      </thead>
                      <tbody className='text-gray-800 align-middle fs-7'>
                      { values.roiDetails.map((item, idx) => (
                        <tr key={`itemDetailRow${idx}`}>
                          <td className="p-2 align-top">
                            <FormikTextInput size={12} name={`roiDetails.${idx}.itemNo`} addClass={'w-100px mb-1 bg-transparent fw-bold'} readOnly />
                            <FormikTextInput size={12} name={`roiDetails.${idx}.labelFrom`} addClass={'w-100px mb-1 bg-transparent'} readOnly />
                          </td>
                          <td className="p-2 align-top">
                            <FormikTextInput size={12} name={`roiDetails.${idx}.quantity`} addClass={'w-100px bg-transparent'} readOnly />
                            <FormikTextInput size={12} name={`roiDetails.${idx}.labelTo`} addClass={'w-100px bg-transparent'} readOnly />
                          </td>
                          <td className="p-2 align-top">
                            <FormikTextInput size={12} name={`roiDetails.${idx}.size`} addClass={'w-275px mb-1'}  title="Edit Size"/>
                            <FormikTextInput size={12} name={`roiDetails.${idx}.type`} addClass={'w-275px mb-1'} title= "Edit Type" />
                          </td>
                          <td className="p-2 align-top">
                            <FormikTextInput size={12} name={`roiDetails.${idx}.remarks`} addClass={'w-125px mb-1'}  title="Remarks"/>
                          </td>
                        </tr>
                        ))
                      }
                      </tbody>
                    </table>
                  </div>
                  <div className='separator border-gray-200 mb-2'></div>
                  </>
                  )}
                />
              </div>
              <StatusErrorMessage status={status} />
              <SubmitButtonRow doClose={doClose} submitText="Generate ROI" cancelText="Discard"/>
            </Form>
          )}
          </Formik>
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {GenerateROIModal}